import React, { useState, useRef, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUserStore } from "../../store/user.store";
import {
  getChefMenu,
  addMenuItem,
  updateMenuItem,
  deleteMenuItem,
} from "../../services/kitchen.service"; 
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { toast } from "react-hot-toast";
import ImageUpload from "../../components/ImageUpload";
import { Clock, Eye, EyeOff, Plus, Search, ChevronDown, ChevronUp, X, ImageOff } from "lucide-react";

const uid = () => Math.random().toString(36).slice(2);

const categories = [
  "Main Course", "Starter", "Salad", "Beverage", "Dessert",
  "Combo", "Soup", "Bread", "Rice", "Snack", "Chaat", "Breakfast","lunch","dinner","evening","morning"
];

// --- TYPE DEFINITIONS ---
interface CustomizationOption {
  key: string;
  name: string;
  extraPrice: number;
  photoUrl: string;
  isActive?: boolean;
  customizations?: CustomizationLevel[];
}

interface CustomizationLevel {
  key: string;
  label: string;
  isActive?: boolean;
  options: CustomizationOption[];
}

interface MenuItemForm {
  name: string;
  description: string;
  basePrice: number;
  categories: string[];
  photoUrl: string;
  isAvailable: boolean;
  customizations?: CustomizationLevel[];
  
  // ✅ NEW TIMING FIELDS
  prepTimeMinutes?: number;
  dailyCutoffTime?: string; // "HH:MM" 24h format
  showPrepTime?: boolean;
}

interface MenuItemWithId extends MenuItemForm {
  id: string;
  chefId: string;
  price?: number; 
}

interface ManageMenuProps {
  kitchenId: string;
  chefUid?: string;
  kitchenName?: string;
}

const getInitialNewItem = (): MenuItemForm => ({
  name: "",
  description: "",
  basePrice: 0,
  categories: [],
  photoUrl: "",
  isAvailable: true,
  customizations: [],
  // ✅ Default Defaults
  prepTimeMinutes: 15,
  dailyCutoffTime: "",
  showPrepTime: true
});

const ManageMenu: React.FC<ManageMenuProps> = ({ kitchenId, chefUid, kitchenName }) => {
  const { userProfile } = useUserStore();
  const queryClient = useQueryClient();
  const formRef = useRef<HTMLFormElement>(null);

  const activeChefId = chefUid || userProfile?.uid;

  const [newItem, setNewItem] = useState<MenuItemForm>(getInitialNewItem());
  const [customLevels, setCustomLevels] = useState<CustomizationLevel[]>([]);
  const [editingItem, setEditingItem] = useState<MenuItemWithId | null>(null);

  // List-first UX
  const [showForm, setShowForm] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [availabilityFilter, setAvailabilityFilter] = useState<"all" | "active" | "inactive">("all");
  const formSectionRef = useRef<HTMLDivElement>(null);

  const {
    data: menu = [],
    isLoading,
    isError,
  } = useQuery<MenuItemWithId[]>(
    ["chefMenu", kitchenId],
    () => getChefMenu(kitchenId),
    { enabled: !!kitchenId }
  );

  // --- MUTATIONS ---
  const { mutate: addItem, isLoading: isAdding } = useMutation(addMenuItem, {
    onSuccess: () => {
      toast.success("Dish added!");
      queryClient.invalidateQueries(["chefMenu", kitchenId]);
      setEditingItem(null);
      setNewItem(getInitialNewItem());
      setCustomLevels([]);
      setShowAdvanced(false);
      setShowForm(false);
    },
    onError: () => toast.error("Failed to add dish."),
  });

  const { mutate: updateItem, isLoading: isUpdating } = useMutation(updateMenuItem, {
    onSuccess: () => {
      toast.success("Dish updated!");
      queryClient.invalidateQueries(["chefMenu", kitchenId]);
      setEditingItem(null);
      setNewItem(getInitialNewItem());
      setCustomLevels([]);
      setShowAdvanced(false);
      setShowForm(false);
    },
    onError: () => toast.error("Failed to update dish."),
  });

  const { mutate: deleteItem } = useMutation(deleteMenuItem, {
    onSuccess: () => {
      toast.success("Menu item deleted!");
      queryClient.invalidateQueries(["chefMenu", kitchenId]);
    },
    onError: () => toast.error("Failed to delete menu item."),
  });

  // ---- Helper function to update nested state (PRESERVED) ----
  const updateNestedCustomization = (path: number[], updater: (levels: CustomizationLevel[]) => void) => {
    const updated = JSON.parse(JSON.stringify(customLevels)); 
    let current: any = updated;

    for (let i = 0; i < path.length; i += 2) {
      const levelIdx = path[i];
      const optionIdx = path[i + 1];

      if (i === path.length - 2) {
        if (!current[levelIdx].options[optionIdx].customizations) {
          current[levelIdx].options[optionIdx].customizations = [];
        }
        updater(current[levelIdx].options[optionIdx].customizations);
      } else {
        current = current[levelIdx].options[optionIdx].customizations;
      }
    }
    setCustomLevels(updated);
  };

  // ---- Recursive rendering component (PRESERVED) ----
  const renderCustomizationLevel = (
    levels: CustomizationLevel[],
    path: number[] = [],
    depth: number = 0
  ) => {
    const bgColors = ['bg-gray-50', 'bg-blue-50', 'bg-green-50', 'bg-yellow-50', 'bg-purple-50'];
    const bgColor = bgColors[depth % bgColors.length];
    const indentClass = depth > 0 ? `ml-${depth * 4}` : '';

    return levels.map((level, levelIdx) => (
      <div key={level.key} className={`border rounded-lg p-4 mb-4 ${indentClass} ${bgColor}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className="font-semibold">
              {depth === 0 ? `Step ${levelIdx + 1}` : `Sub-level`} Label:
            </span>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={level.isActive !== false}
                onChange={(e) => {
                  if (path.length === 0) {
                    const updated = [...customLevels];
                    updated[levelIdx] = { ...updated[levelIdx], isActive: e.target.checked };
                    setCustomLevels(updated);
                  } else {
                    updateNestedCustomization(path, (parentLevels) => {
                      parentLevels[levelIdx] = { ...parentLevels[levelIdx], isActive: e.target.checked };
                    });
                  }
                }}
                className="sr-only"
              />
              <div className={`relative inline-flex items-center h-5 rounded-full w-9 transition-colors ${
                level.isActive !== false ? 'bg-green-500' : 'bg-gray-300'
              }`}>
                <span className={`inline-block w-3 h-3 transform bg-white rounded-full transition-transform ${
                  level.isActive !== false ? 'translate-x-5' : 'translate-x-1'
                }`} />
              </div>
              <span className={`ml-2 text-xs font-medium ${
                level.isActive !== false ? 'text-green-600' : 'text-gray-500'
              }`}>
                {level.isActive !== false ? 'Active' : 'Inactive'}
              </span>
            </label>
          </div>
          
          <Button
            variant="danger"
            type="button"
            onClick={() => {
              if (path.length === 0) {
                setCustomLevels(customLevels.filter((_, idx) => idx !== levelIdx));
              } else {
                updateNestedCustomization(path, (parentLevels) => {
                  parentLevels.splice(levelIdx, 1);
                });
              }
            }}
          >
            Delete Level
          </Button>
        </div>
        
        <input
          type="text"
          className="form-control mb-2"
          value={level.label}
          onChange={e => {
            if (path.length === 0) {
              const updated = [...customLevels];
              updated[levelIdx].label = e.target.value;
              setCustomLevels(updated);
            } else {
              updateNestedCustomization(path, (parentLevels) => {
                parentLevels[levelIdx].label = e.target.value;
              });
            }
          }}
          placeholder="e.g. Variant, Cut Style, Add-ons"
        />
        
        <div className="flex items-center mb-2">
          <span className="font-medium">Options:</span>
          <Button
            variant="secondary"
            className="ml-2"
            type="button"
            onClick={() => {
              const newOption = {
                key: uid(),
                name: "",
                extraPrice: 0,
                photoUrl: "",
                isActive: true,
                customizations: [],
              };

              if (path.length === 0) {
                const updated = [...customLevels];
                updated[levelIdx].options.push(newOption);
                setCustomLevels(updated);
              } else {
                updateNestedCustomization(path, (parentLevels) => {
                  parentLevels[levelIdx].options.push(newOption);
                });
              }
            }}
          >
            Add Option
          </Button>
        </div>
        
        {level.options.map((option, optionIdx) => (
          <div key={option.key} className="flex flex-col bg-white rounded border p-3 mb-2 ml-2 shadow-sm">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <label className="flex items-center cursor-pointer" title={option.isActive !== false ? "Active" : "Inactive"}>
                <input
                  type="checkbox"
                  checked={option.isActive !== false}
                  onChange={(e) => {
                    if (path.length === 0) {
                      const updated = [...customLevels];
                      updated[levelIdx].options[optionIdx].isActive = e.target.checked;
                      setCustomLevels(updated);
                    } else {
                      updateNestedCustomization(path, (parentLevels) => {
                        parentLevels[levelIdx].options[optionIdx].isActive = e.target.checked;
                      });
                    }
                  }}
                  className="sr-only"
                />
                <div className={`relative inline-flex items-center h-5 rounded-full w-9 transition-colors ${
                  option.isActive !== false ? 'bg-green-500' : 'bg-gray-300'
                }`}>
                  <span className={`inline-block w-3 h-3 transform bg-white rounded-full transition-transform ${
                    option.isActive !== false ? 'translate-x-5' : 'translate-x-1'
                  }`} />
                </div>
              </label>
              
              <input
                className="form-control"
                type="text"
                value={option.name}
                placeholder="Option name"
                onChange={e => {
                  if (path.length === 0) {
                    const updated = [...customLevels];
                    updated[levelIdx].options[optionIdx].name = e.target.value;
                    setCustomLevels(updated);
                  } else {
                    updateNestedCustomization(path, (parentLevels) => {
                      parentLevels[levelIdx].options[optionIdx].name = e.target.value;
                    });
                  }
                }}
                style={{ minWidth: 120 }}
              />
              <input
                className="form-control"
                type="number"
                value={option.extraPrice}
                placeholder="Extra Price"
                min={0}
                onChange={e => {
                  if (path.length === 0) {
                    const updated = [...customLevels];
                    updated[levelIdx].options[optionIdx].extraPrice = Number(e.target.value);
                    setCustomLevels(updated);
                  } else {
                    updateNestedCustomization(path, (parentLevels) => {
                      parentLevels[levelIdx].options[optionIdx].extraPrice = Number(e.target.value);
                    });
                  }
                }}
                style={{ width: 90 }}
              />
              <div>
                <ImageUpload
                  onUpload={url => {
                    if (path.length === 0) {
                      const updated = [...customLevels];
                      updated[levelIdx].options[optionIdx].photoUrl = url;
                      setCustomLevels(updated);
                    } else {
                      updateNestedCustomization(path, (parentLevels) => {
                        parentLevels[levelIdx].options[optionIdx].photoUrl = url;
                      });
                    }
                  }}
                  currentphotoUrl={option.photoUrl}
                  kitchenId={kitchenId}
                  dishName={option.name || newItem.name}
                  description={
                    option.name
                      ? `${newItem.name || "Dish"} customization: ${option.name}`
                      : newItem.description
                  }
                  kitchenName={kitchenName}
                />
              </div>
              <Button
                variant="secondary"
                type="button"
                onClick={() => {
                  if (path.length === 0) {
                    const updated = [...customLevels];
                    updated[levelIdx].options.splice(optionIdx + 1, 0, {
                      ...JSON.parse(JSON.stringify(updated[levelIdx].options[optionIdx])),
                      key: uid(),
                    });
                    setCustomLevels(updated);
                  } else {
                    updateNestedCustomization(path, (parentLevels) => {
                      const opt = parentLevels[levelIdx].options[optionIdx];
                      parentLevels[levelIdx].options.splice(optionIdx + 1, 0, {
                        ...JSON.parse(JSON.stringify(opt)),
                        key: uid(),
                      });
                    });
                  }
                }}
              >
                Copy
              </Button>
              <Button
                variant="danger"
                type="button"
                onClick={() => {
                  if (path.length === 0) {
                    const updated = [...customLevels];
                    updated[levelIdx].options = updated[levelIdx].options.filter((_, idx) => idx !== optionIdx);
                    setCustomLevels(updated);
                  } else {
                    updateNestedCustomization(path, (parentLevels) => {
                      parentLevels[levelIdx].options = parentLevels[levelIdx].options.filter((_, idx) => idx !== optionIdx);
                    });
                  }
                }}
              >
                Delete
              </Button>
              <Button
                variant="secondary"
                type="button"
                onClick={() => {
                  const newSubLevel = {
                    key: uid(),
                    label: "",
                    isActive: true,
                    options: [],
                  };
                  if (path.length === 0) {
                    const updated = [...customLevels];
                    if (!updated[levelIdx].options[optionIdx].customizations) {
                      updated[levelIdx].options[optionIdx].customizations = [];
                    }
                    updated[levelIdx].options[optionIdx].customizations!.push(newSubLevel);
                    setCustomLevels(updated);
                  } else {
                    updateNestedCustomization(path, (parentLevels) => {
                      if (!parentLevels[levelIdx].options[optionIdx].customizations) {
                        parentLevels[levelIdx].options[optionIdx].customizations = [];
                      }
                      parentLevels[levelIdx].options[optionIdx].customizations!.push(newSubLevel);
                    });
                  }
                }}
              >
                Add Sub-level
              </Button>
            </div>
            
            {/* Recursive render of sub-customizations */}
            {option.customizations && option.customizations.length > 0 && (
              <div className="mt-2">
                {renderCustomizationLevel(
                  option.customizations,
                  [...path, levelIdx, optionIdx],
                  depth + 1
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    ));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!kitchenId) {
      toast.error("No kitchen selected");
      return;
    }

    const trimmedName = (newItem.name || "").trim();
    if (!trimmedName) {
      toast.error("Dish name is required");
      return;
    }
    if (newItem.basePrice === undefined || newItem.basePrice === null || Number.isNaN(Number(newItem.basePrice)) || Number(newItem.basePrice) < 0) {
      toast.error("Enter a valid base price");
      return;
    }

    const basePrice = Number(newItem.basePrice);
    const itemData = {
      ...newItem,
      name: trimmedName,
      description: (newItem.description || "").trim(),
      basePrice,
      price: basePrice, // cart/customer often read price
      customizations: customLevels,
      chefId: activeChefId || "admin_override",
    };

    if (editingItem) {
      updateItem({
        groupId: kitchenId,
        itemName: editingItem.id,
        itemObj: itemData,
      });
    } else {
      addItem({
        groupId: kitchenId,
        itemName: trimmedName.replace(/[^a-zA-Z0-9]/g, "_") + `_${uid()}`,
        itemObj: itemData,
      });
    }
  };

  const handleEditClick = (itemToEdit: MenuItemWithId) => {
    setEditingItem(itemToEdit);
    setNewItem({
      name: itemToEdit.name,
      description: itemToEdit.description || "",
      basePrice: itemToEdit.basePrice ?? itemToEdit.price ?? 0,
      categories: itemToEdit.categories || [],
      photoUrl: itemToEdit.photoUrl || "",
      isAvailable: itemToEdit.isAvailable !== false,
      customizations: itemToEdit.customizations || [],
      prepTimeMinutes: itemToEdit.prepTimeMinutes || 15,
      dailyCutoffTime: itemToEdit.dailyCutoffTime || "",
      showPrepTime: itemToEdit.showPrepTime !== false,
    });
    setCustomLevels(itemToEdit.customizations || []);
    const hasAdvanced =
      (itemToEdit.customizations && itemToEdit.customizations.length > 0) ||
      Boolean(itemToEdit.dailyCutoffTime) ||
      (itemToEdit.prepTimeMinutes != null && itemToEdit.prepTimeMinutes !== 15);
    setShowAdvanced(Boolean(hasAdvanced));
    setShowForm(true);
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setNewItem(getInitialNewItem());
    setCustomLevels([]);
    setShowAdvanced(false);
    setShowForm(false);
  };

  const handleOpenAdd = () => {
    setEditingItem(null);
    setNewItem(getInitialNewItem());
    setCustomLevels([]);
    setShowAdvanced(false);
    setShowForm(true);
  };

  useEffect(() => {
    if (showForm) {
      formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [showForm, editingItem?.id]);

  const filteredMenu = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return menu.filter((item) => {
      const available = item.isAvailable !== false;
      if (availabilityFilter === "active" && !available) return false;
      if (availabilityFilter === "inactive" && available) return false;
      if (q && !(item.name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [menu, searchQuery, availabilityFilter]);

  const activeCount = menu.filter((i) => i.isAvailable !== false).length;
  const inactiveCount = menu.length - activeCount;
  const isSaving = isAdding || isUpdating;

  // ---- Recursive preview component (PRESERVED) ----
  function renderCustomizationPreview(levels: CustomizationLevel[], depth: number): React.ReactNode {
    const indentClass = depth > 0 ? `ml-${depth * 4}` : '';
    
    return levels.map((level, idx) => (
      <div key={level.key || idx} className={`${indentClass} mt-2`}>
        <div className="flex items-center gap-2">
          <strong className="text-sm">{level.label}:</strong>
          {level.isActive === false && (
            <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">Level Inactive</span>
          )}
        </div>
        <ul className="ml-4 text-sm">
          {level.options.map((opt, optIdx) => (
            <li 
              key={opt.key || optIdx} 
              className={opt.isActive === false ? 'text-gray-400' : ''}
            >
              <span className={opt.isActive === false ? 'line-through' : ''}>
                {opt.name} {opt.extraPrice > 0 && `(+₹${opt.extraPrice})`}
              </span>
              {opt.isActive === false && (
                <span className="text-xs ml-2 px-2 py-0.5 bg-red-100 text-red-600 rounded">Inactive</span>
              )}
              {opt.photoUrl && (
                <img 
                  src={opt.photoUrl} 
                  alt={opt.name} 
                  width={30} 
                  height={30} 
                  className="inline ml-2 rounded" 
                />
              )}
              {opt.customizations && opt.customizations.length > 0 && (
                <div className="ml-4">
                  {renderCustomizationPreview(opt.customizations, depth + 1)}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    ));
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-28">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold">Manage Menu</h1>
          <p className="text-sm text-gray-500 mt-1">
            {activeCount} active / {inactiveCount} inactive
          </p>
        </div>
        <Button
          type="button"
          onClick={handleOpenAdd}
          className="shrink-0 hidden sm:inline-flex items-center gap-1.5"
          size="sm"
        >
          <Plus size={16} />
          Add dish
        </Button>
      </div>

      {/* Sticky add on mobile */}
      <div className="sm:hidden sticky top-0 z-20 -mx-4 px-4 py-2 bg-white/95 backdrop-blur border-b mb-4">
        <Button
          type="button"
          onClick={handleOpenAdd}
          className="w-full flex items-center justify-center gap-2 min-h-[44px]"
        >
          <Plus size={18} />
          Add dish
        </Button>
      </div>

      {/* Search + Active/Inactive filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search dishes by name…"
            className="w-full border rounded-lg pl-9 pr-3 py-2.5 text-sm min-h-[44px] focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
        </div>
        <div className="flex rounded-lg border overflow-hidden self-start">
          {(
            [
              ["all", "All"],
              ["active", "Active"],
              ["inactive", "Inactive"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setAvailabilityFilter(value)}
              className={`px-3 py-2.5 text-sm font-medium min-h-[44px] transition-colors ${
                availabilityFilter === value
                  ? "bg-orange-500 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* LIST FIRST */}
      <section className="mb-6">
        <h2 className="text-lg font-semibold mb-3">Your dishes</h2>
        {isLoading ? (
          <p className="text-gray-500 py-8 text-center">Loading menu…</p>
        ) : isError ? (
          <p className="text-red-500 py-8 text-center">Could not load menu.</p>
        ) : menu.length === 0 ? (
          <Card className="p-6 text-center">
            <p className="text-gray-600 mb-3">No dishes yet.</p>
            <Button type="button" onClick={handleOpenAdd}>
              <Plus size={16} className="inline mr-1" />
              Add your first dish
            </Button>
          </Card>
        ) : filteredMenu.length === 0 ? (
          <p className="text-gray-500 py-6 text-center">
            No dishes match your search/filter.
          </p>
        ) : (
          <div className="space-y-3">
            {filteredMenu.map((item: MenuItemWithId) => {
              const price = item.basePrice ?? item.price ?? 0;
              const available = item.isAvailable !== false;
              return (
                <Card
                  key={item.id}
                  className="p-4 border-l-4 border-l-transparent hover:border-l-orange-400 transition-all"
                >
                  <div className="flex gap-3">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                      {item.photoUrl ? (
                        <img
                          src={item.photoUrl}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex flex-col items-center text-gray-400 px-1">
                          <ImageOff size={18} />
                          <span className="text-[10px] mt-0.5 leading-tight text-center">
                            No photo
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-base sm:text-lg truncate">
                            {item.name}
                          </p>
                          <p className="font-bold text-orange-600 text-lg">
                            ₹{price}
                          </p>
                        </div>
                        <label className="inline-flex items-center cursor-pointer shrink-0">
                          <span
                            className={`mr-2 text-xs font-semibold ${
                              available ? "text-green-600" : "text-gray-500"
                            }`}
                          >
                            {available ? "Active" : "Inactive"}
                          </span>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={available}
                            onClick={() => {
                              const p = item.basePrice ?? item.price ?? 0;
                              updateItem({
                                groupId: kitchenId,
                                itemName: item.id,
                                itemObj: {
                                  ...item,
                                  isAvailable: !available,
                                  basePrice: p,
                                  price: p,
                                },
                              });
                            }}
                            className={`relative inline-flex items-center h-7 rounded-full w-12 transition-colors ${
                              available ? "bg-green-500" : "bg-gray-300"
                            }`}
                          >
                            <span
                              className={`inline-block w-5 h-5 transform bg-white rounded-full transition-transform shadow ${
                                available ? "translate-x-6" : "translate-x-1"
                              }`}
                            />
                          </button>
                        </label>
                      </div>

                      {item.prepTimeMinutes ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded mt-1">
                          <Clock size={10} /> {item.prepTimeMinutes}m
                        </span>
                      ) : null}

                      {item.categories && item.categories.length > 0 && (
                        <div className="flex gap-1.5 flex-wrap mt-1.5">
                          {item.categories.map((cat: string) => (
                            <span
                              key={cat}
                              className="px-2 py-0.5 rounded bg-gray-100 text-xs font-medium text-gray-700"
                            >
                              {cat}
                            </span>
                          ))}
                        </div>
                      )}

                      {item.customizations && item.customizations.length > 0 && (
                        <div className="mt-2 text-xs text-gray-600">
                          {renderCustomizationPreview(item.customizations, 0)}
                        </div>
                      )}

                      <div className="flex gap-2 mt-3">
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          className="min-h-[44px] px-4"
                          onClick={() => handleEditClick(item)}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          className="min-h-[44px] px-4"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Delete "${item.name}"? This cannot be undone.`
                              )
                            ) {
                              deleteItem({
                                groupId: kitchenId,
                                itemName: item.id,
                              });
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* Compact create/edit panel */}
      {showForm && (
        <div
          ref={formSectionRef}
          className="border-2 border-orange-200 rounded-xl bg-orange-50/40 p-4 sm:p-5 scroll-mt-20"
        >
          {editingItem && (
            <div className="mb-4 flex items-center justify-between gap-2 bg-amber-100 border border-amber-300 text-amber-900 rounded-lg px-3 py-2.5">
              <span className="text-sm font-semibold">
                Editing: {editingItem.name}
              </span>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="text-amber-800 hover:text-amber-950 p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Cancel edit"
              >
                <X size={18} />
              </button>
            </div>
          )}

          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">
              {editingItem ? "Update dish" : "Quick add dish"}
            </h2>
            {!editingItem && (
              <button
                type="button"
                onClick={handleCancelEdit}
                className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 min-h-[44px]"
              >
                <X size={14} /> Close
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Required: name + price. Category and photo are optional.
          </p>

          <form className="space-y-4" onSubmit={handleSubmit} ref={formRef}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dish name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2.5 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-orange-400"
                  value={newItem.name}
                  onChange={(e) =>
                    setNewItem({ ...newItem, name: e.target.value })
                  }
                  placeholder="e.g. Chapati"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Base price (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  className="w-full border rounded-lg px-3 py-2.5 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-orange-400"
                  value={newItem.basePrice || ""}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      basePrice: Number(e.target.value),
                    })
                  }
                  placeholder="50"
                  required
                  min={0}
                  step="1"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categories{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto p-1">
                {categories.map((cat) => {
                  const selected = newItem.categories.includes(cat);
                  return (
                    <button
                      type="button"
                      key={cat}
                      className={`px-3 py-1.5 rounded-full border transition text-xs min-h-[36px] ${
                        selected
                          ? "bg-orange-500 text-white font-semibold border-orange-600"
                          : "bg-white text-gray-800 border-gray-300"
                      }`}
                      onClick={() =>
                        setNewItem((prev) => ({
                          ...prev,
                          categories: selected
                            ? prev.categories.filter((c) => c !== cat)
                            : [...prev.categories, cat],
                        }))
                      }
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                rows={2}
                value={newItem.description}
                onChange={(e) =>
                  setNewItem({ ...newItem, description: e.target.value })
                }
                placeholder="Short note for customers (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Photo{" "}
                <span className="text-gray-400 font-normal">
                  (optional — Generate with AI or upload)
                </span>
              </label>
              <ImageUpload
                onUpload={(url) =>
                  setNewItem((prev) => ({ ...prev, photoUrl: url }))
                }
                currentphotoUrl={newItem.photoUrl}
                kitchenId={kitchenId}
                dishName={newItem.name}
                description={newItem.description}
                kitchenName={kitchenName}
              />
              {newItem.photoUrl && (
                <img
                  src={newItem.photoUrl}
                  alt="Preview"
                  className="mt-2 h-28 rounded-lg object-cover"
                />
              )}
            </div>

            {/* Advanced accordion — closed by default */}
            <div className="border rounded-lg bg-white overflow-hidden">
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 text-left font-medium text-sm text-gray-800 hover:bg-gray-50 min-h-[44px]"
              >
                <span>
                  More options{" "}
                  <span className="text-gray-400 font-normal">
                    (prep time, cutoff, customizations)
                  </span>
                </span>
                {showAdvanced ? (
                  <ChevronUp size={18} />
                ) : (
                  <ChevronDown size={18} />
                )}
              </button>

              {showAdvanced && (
                <div className="px-4 pb-4 space-y-4 border-t">
                  <div className="bg-orange-50 p-4 rounded-xl border border-orange-100 space-y-4 mt-3">
                    <div className="flex items-center gap-2 text-orange-800">
                      <Clock size={18} />
                      <span className="font-bold text-sm uppercase">
                        Timing & availability
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                          Prep time (mins)
                        </label>
                        <input
                          type="number"
                          className="w-full border p-2 rounded bg-white focus:outline-none focus:border-orange-500 min-h-[44px]"
                          value={newItem.prepTimeMinutes}
                          onChange={(e) =>
                            setNewItem({
                              ...newItem,
                              prepTimeMinutes: Number(e.target.value),
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                          Order cut-off
                        </label>
                        <input
                          type="time"
                          className="w-full border p-2 rounded bg-white focus:outline-none focus:border-orange-500 min-h-[44px]"
                          value={newItem.dailyCutoffTime || ""}
                          onChange={(e) =>
                            setNewItem({
                              ...newItem,
                              dailyCutoffTime: e.target.value,
                            })
                          }
                        />
                        <p className="text-[10px] text-gray-500 mt-1">
                          Leave blank for no cut-off
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 pt-1">
                      <input
                        type="checkbox"
                        id="showPrep"
                        checked={!!newItem.showPrepTime}
                        onChange={(e) =>
                          setNewItem({
                            ...newItem,
                            showPrepTime: e.target.checked,
                          })
                        }
                        className="w-4 h-4 text-orange-600 rounded"
                      />
                      <label
                        htmlFor="showPrep"
                        className="text-sm text-gray-700 flex items-center gap-1 cursor-pointer"
                      >
                        {newItem.showPrepTime ? (
                          <Eye size={14} />
                        ) : (
                          <EyeOff size={14} />
                        )}
                        Show prep time to customers
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="font-semibold mb-1 block text-sm">
                      Customizations
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Optional variants (Size, Spice) or add-ons.
                    </p>
                    <div className="space-y-4">
                      {renderCustomizationLevel(customLevels)}
                      {customLevels.length < 10 && (
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => {
                            setCustomLevels([
                              ...customLevels,
                              {
                                key: uid(),
                                label: "",
                                isActive: true,
                                options: [],
                              },
                            ]);
                          }}
                        >
                          + Add customization level
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                type="submit"
                className="w-full sm:flex-1 min-h-[48px]"
                disabled={isSaving}
              >
                {isSaving
                  ? "Saving…"
                  : editingItem
                    ? "Save changes"
                    : "Add dish"}
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="w-full sm:w-auto min-h-[48px]"
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ManageMenu;
